/**
 * Sync Queue Service
 *
 * Manages offline change queueing and synchronization with the server.
 *
 * Two queue implementations:
 * 1. SyncQueue (legacy) — journal-entry specific, uses entryDate+contentHash dedup
 * 2. ExtendedSyncQueue — multi-module support with module discriminator routing
 *
 * Both share the same underlying IndexedDB syncQueue store (max 1000 items combined).
 *
 * @module syncQueue
 * @see Requirements 5.4, 8.1, 8.4, 12.6, 12.7, 12.8
 */

import type {
  SyncQueueItem,
  LocalJournalEntry,
  JournalEntry,
  SyncResponse,
  ExtendedSyncQueueItem,
} from '../types';
import { SyncAction, SyncStatus } from '../types/enums';
import {
  db,
  enqueueSyncItem,
  dequeueSyncItem,
  getSyncQueueSize,
  getEntry,
  putEntry,
} from './localDb';

// ─── Constants ───────────────────────────────────────────────────────────────

/** Maximum number of items allowed in the sync queue across all modules. */
export const MAX_SYNC_QUEUE_SIZE = 1000;

/** Initial backoff delay in milliseconds. */
const INITIAL_BACKOFF_MS = 1000;

/** Maximum backoff delay in milliseconds. */
const MAX_BACKOFF_MS = 30_000;

/** Maximum time (ms) after connectivity detected to begin syncing. */
const RECONNECT_SYNC_DELAY_MS = 10_000;

// ─── Module Endpoint Routing ─────────────────────────────────────────────────

/**
 * Maps module identifiers to their corresponding API endpoints.
 */
export const MODULE_ENDPOINTS: Record<ExtendedSyncQueueItem['module'], string> = {
  journal: '/entries',
  tasks: '/tasks',
  habits: '/habits',
  pomodoro: '/pomodoro/sessions',
  countdown: '/countdowns',
  calendar: '/calendar/events',
  notes: '/notes',
};

/**
 * Maps sync actions to HTTP methods.
 */
export const ACTION_TO_METHOD: Record<ExtendedSyncQueueItem['action'], string> = {
  create: 'POST',
  update: 'PUT',
  delete: 'DELETE',
};

// ─── Legacy Types (Journal-specific) ─────────────────────────────────────────

/**
 * Injectable API functions for server communication (journal-specific).
 */
export interface SyncApiClient {
  /** Fetch entries modified since the given ISO 8601 timestamp. */
  fetchSyncEntries(since: string): Promise<SyncResponse>;

  /** Push a single sync queue item to the server. Returns the updated entry on success. */
  pushEntry(item: SyncQueueItem): Promise<JournalEntry>;
}

/** Callback invoked when a conflict is detected during sync. */
export type ConflictCallback = (
  localEntry: LocalJournalEntry,
  serverEntry: JournalEntry
) => void;

/** Callback invoked when the sync queue finishes processing all items. */
export type SyncCompleteCallback = (serverTime: string) => void;

/** Options for constructing the legacy SyncQueue. */
export interface SyncQueueOptions {
  /** API client for server communication. */
  apiClient: SyncApiClient;
  /** Called when a conflict is detected between local and server versions. */
  onConflict?: ConflictCallback;
  /** Called when the sync queue has been fully processed. */
  onSyncComplete?: SyncCompleteCallback;
  /** Called when the queue size changes. */
  onQueueSizeChange?: (size: number) => void;
  /**
   * Custom delay function for backoff waits. Defaults to setTimeout-based delay.
   * Useful for testing to avoid real delays.
   */
  delayFn?: (ms: number) => Promise<void>;
  /**
   * Custom reconnect delay in ms. Defaults to a random value up to RECONNECT_SYNC_DELAY_MS.
   * Set to 0 for immediate reconnect in tests.
   */
  reconnectDelayMs?: number;
}

// ─── Legacy SyncQueue Service Class (Journal-specific) ───────────────────────

/**
 * SyncQueue manages the offline sync queue for journal entries.
 *
 * It enqueues local changes, detects connectivity changes, fetches
 * server updates on reconnect, detects conflicts, and processes
 * queued items sequentially with exponential backoff.
 */
export class SyncQueue {
  private apiClient: SyncApiClient;
  private onConflict?: ConflictCallback;
  private onSyncComplete?: SyncCompleteCallback;
  private onQueueSizeChange?: (size: number) => void;
  private delayFn: (ms: number) => Promise<void>;
  private reconnectDelayMs?: number;

  private isProcessing = false;
  private isStopped = false;
  private currentBackoff = INITIAL_BACKOFF_MS;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private backoffTimer: ReturnType<typeof setTimeout> | null = null;
  private lastSyncTime: string | null = null;

  constructor(options: SyncQueueOptions) {
    this.apiClient = options.apiClient;
    this.onConflict = options.onConflict;
    this.onSyncComplete = options.onSyncComplete;
    this.onQueueSizeChange = options.onQueueSizeChange;
    this.reconnectDelayMs = options.reconnectDelayMs;
    this.delayFn = options.delayFn ?? ((ms) => new Promise((resolve) => {
      setTimeout(resolve, ms);
    }));
  }

  // ─── Public Methods ──────────────────────────────────────────────────────

  /**
   * Enqueue a change to the sync queue with FIFO ordering.
   * Deduplicates by content hash: if an existing queued item for the same
   * entryDate has the same contentHash, the enqueue is skipped.
   *
   * @param entryDate - The date of the entry that changed (YYYY-MM-DD)
   * @param action - The type of change (create, update, delete)
   * @param payload - The partial entry data representing the change
   * @throws Error if the queue is already at max capacity (1000 items)
   */
  async enqueue(
    entryDate: string,
    action: SyncAction,
    payload: Partial<LocalJournalEntry>
  ): Promise<void> {
    // Deduplicate: skip if an existing item with same entryDate+contentHash exists
    if (payload.contentHash) {
      const isDuplicate = await this.isDuplicateItem(entryDate, payload.contentHash);
      if (isDuplicate) {
        return;
      }
    }

    const item: Omit<SyncQueueItem, 'id'> = {
      entryDate,
      action,
      timestamp: new Date().toISOString(),
      payload,
      retryCount: 0,
    };

    // enqueueSyncItem enforces the capacity limit
    await enqueueSyncItem(item);
    await this.notifyQueueSizeChange();
  }

  /**
   * Get the current number of items in the queue.
   */
  async getQueueSize(): Promise<number> {
    return getSyncQueueSize();
  }

  /**
   * Set the last known sync time. Used when restoring state after app restart.
   */
  setLastSyncTime(time: string | null): void {
    this.lastSyncTime = time;
  }

  /**
   * Get the last known sync time.
   */
  getLastSyncTime(): string | null {
    return this.lastSyncTime;
  }

  /**
   * Handle connectivity being restored. Begins sync within 10 seconds.
   * Fetches server changes, detects conflicts, then processes the queue.
   */
  handleReconnect(): void {
    if (this.isProcessing || this.isStopped) {
      return;
    }

    // Clear any existing reconnect timer
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
    }

    const delay = this.reconnectDelayMs ?? Math.min(
      Math.random() * RECONNECT_SYNC_DELAY_MS,
      RECONNECT_SYNC_DELAY_MS
    );

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      void this.startSync();
    }, delay);
  }

  /**
   * Start the full sync process: fetch server changes, then process queue.
   */
  async startSync(): Promise<void> {
    if (this.isProcessing || this.isStopped) {
      return;
    }

    this.isProcessing = true;
    this.currentBackoff = INITIAL_BACKOFF_MS;

    try {
      await this.fetchServerChanges();
      await this.processQueue();
    } finally {
      this.isProcessing = false;
    }
  }

  /**
   * Stop the sync queue processing. Clears all timers.
   */
  stop(): void {
    this.isStopped = true;
    this.isProcessing = false;

    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.backoffTimer !== null) {
      clearTimeout(this.backoffTimer);
      this.backoffTimer = null;
    }
  }

  /**
   * Resume the sync queue after being stopped.
   */
  resume(): void {
    this.isStopped = false;
  }

  /**
   * Check if the queue is currently processing.
   */
  get processing(): boolean {
    return this.isProcessing;
  }

  // ─── Private Methods ─────────────────────────────────────────────────────

  private async isDuplicateItem(
    entryDate: string,
    contentHash: string
  ): Promise<boolean> {
    // The syncQueue store (v2+) is indexed on '++id, module, entityId, timestamp'
    // — there is no 'entryDate' index. Fetch all items via a supported path and
    // filter in memory to avoid a SchemaError.
    const all = await db.syncQueue.toArray();
    return all.some((i) => {
      const it = i as unknown as SyncQueueItem;
      return it.entryDate === entryDate && it.payload?.contentHash === contentHash;
    });
  }

  private async fetchServerChanges(): Promise<void> {
    const since = this.lastSyncTime || new Date(0).toISOString();

    let response: SyncResponse;
    try {
      response = await this.apiClient.fetchSyncEntries(since);
    } catch {
      return;
    }

    for (const serverEntry of response.entries) {
      const localEntry = await getEntry(serverEntry.entryDate);

      if (localEntry && this.hasConflict(localEntry, serverEntry)) {
        this.onConflict?.(localEntry, serverEntry);
      } else if (localEntry) {
        if (serverEntry.version > localEntry.version) {
          await putEntry({
            ...localEntry,
            content: serverEntry.content,
            version: serverEntry.version,
            updatedAt: serverEntry.updatedAt,
            mediaRefs: serverEntry.mediaRefs,
            contentHash: serverEntry.contentHash,
            syncStatus: SyncStatus.Synced,
          });
        }
      } else {
        await putEntry({
          entryDate: serverEntry.entryDate,
          content: serverEntry.content,
          version: serverEntry.version,
          localVersion: 0,
          updatedAt: serverEntry.updatedAt,
          createdAt: serverEntry.createdAt,
          mediaRefs: serverEntry.mediaRefs,
          syncStatus: SyncStatus.Synced,
          contentHash: serverEntry.contentHash,
          pendingMediaUploads: [],
        });
      }
    }

    this.lastSyncTime = response.serverTime;
  }

  private hasConflict(
    localEntry: LocalJournalEntry,
    serverEntry: JournalEntry
  ): boolean {
    return (
      serverEntry.version > localEntry.version &&
      localEntry.syncStatus === SyncStatus.Pending
    );
  }

  private async processQueue(): Promise<void> {
    while (!this.isStopped) {
      const item = await dequeueSyncItem();
      if (!item) {
        if (this.lastSyncTime) {
          this.onSyncComplete?.(this.lastSyncTime);
        }
        await this.notifyQueueSizeChange();
        break;
      }

      const success = await this.processItem(item);

      if (success) {
        this.currentBackoff = INITIAL_BACKOFF_MS;
        await this.notifyQueueSizeChange();
      } else {
        await this.reEnqueueItem(item);
        await this.notifyQueueSizeChange();
        await this.waitBackoff();
        this.currentBackoff = Math.min(
          this.currentBackoff * 2,
          MAX_BACKOFF_MS
        );
      }
    }
  }

  private async processItem(item: SyncQueueItem): Promise<boolean> {
    try {
      const updatedEntry = await this.apiClient.pushEntry(item);
      const localEntry = await getEntry(item.entryDate);
      if (localEntry) {
        await putEntry({
          ...localEntry,
          version: updatedEntry.version,
          updatedAt: updatedEntry.updatedAt,
          contentHash: updatedEntry.contentHash,
          syncStatus: SyncStatus.Synced,
        });
      }
      return true;
    } catch {
      return false;
    }
  }

  private async reEnqueueItem(item: SyncQueueItem): Promise<void> {
    const newItem: Omit<SyncQueueItem, 'id'> = {
      entryDate: item.entryDate,
      action: item.action,
      timestamp: item.timestamp,
      payload: item.payload,
      retryCount: item.retryCount + 1,
    };
    await db.syncQueue.add(newItem as SyncQueueItem);
  }

  private async waitBackoff(): Promise<void> {
    if (this.isStopped) {
      return;
    }
    await this.delayFn(this.currentBackoff);
  }

  private async notifyQueueSizeChange(): Promise<void> {
    if (this.onQueueSizeChange) {
      const size = await getSyncQueueSize();
      this.onQueueSizeChange(size);
    }
  }
}

// ─── Extended Sync Queue Types ───────────────────────────────────────────────

/**
 * Injectable API client for server communication supporting multi-module sync.
 */
export interface ExtendedSyncApiClient {
  /**
   * Send a sync request to the appropriate module endpoint.
   *
   * @param endpoint - The API endpoint path (e.g., '/tasks', '/habits')
   * @param method - HTTP method (POST, PUT, DELETE)
   * @param entityId - The entity ID (used in PUT/DELETE URL construction)
   * @param payload - The data payload for the request
   * @returns The server response data
   */
  sendRequest(
    endpoint: string,
    method: string,
    entityId: string,
    payload: Record<string, unknown>
  ): Promise<Record<string, unknown>>;
}

/** Callback invoked when the extended sync queue finishes processing all items. */
export type ExtendedSyncCompleteCallback = () => void;

/** Options for constructing the ExtendedSyncQueue. */
export interface ExtendedSyncQueueOptions {
  /** API client for server communication. */
  apiClient: ExtendedSyncApiClient;
  /** Called when the sync queue has been fully processed. */
  onSyncComplete?: ExtendedSyncCompleteCallback;
  /** Called when the queue size changes. */
  onQueueSizeChange?: (size: number) => void;
  /** Called when processing an item fails after retry. */
  onItemError?: (item: ExtendedSyncQueueItem, error: unknown) => void;
  /**
   * Custom delay function for backoff waits. Defaults to setTimeout-based delay.
   */
  delayFn?: (ms: number) => Promise<void>;
  /**
   * Custom reconnect delay in ms. Defaults to a random value up to RECONNECT_SYNC_DELAY_MS.
   * Set to 0 for immediate reconnect in tests.
   */
  reconnectDelayMs?: number;
}

// ─── ExtendedSyncQueue Service Class ─────────────────────────────────────────

/**
 * ExtendedSyncQueue manages the offline sync queue for all Kira modules.
 *
 * It enqueues local changes from any module, processes them in chronological
 * order (by timestamp regardless of module), routes each item to the correct
 * API endpoint based on the module discriminator, and applies exponential
 * backoff on failure with retryCount increment.
 *
 * @see Requirements 12.6 (chronological ordering across modules)
 * @see Requirements 12.7 (conflict handling)
 * @see Requirements 12.8 (1000-item combined limit)
 */
export class ExtendedSyncQueue {
  private apiClient: ExtendedSyncApiClient;
  private onSyncComplete?: ExtendedSyncCompleteCallback;
  private onQueueSizeChange?: (size: number) => void;
  private onItemError?: (item: ExtendedSyncQueueItem, error: unknown) => void;
  private delayFn: (ms: number) => Promise<void>;
  private reconnectDelayMs?: number;

  private isProcessing = false;
  private isStopped = false;
  private currentBackoff = INITIAL_BACKOFF_MS;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(options: ExtendedSyncQueueOptions) {
    this.apiClient = options.apiClient;
    this.onSyncComplete = options.onSyncComplete;
    this.onQueueSizeChange = options.onQueueSizeChange;
    this.onItemError = options.onItemError;
    this.reconnectDelayMs = options.reconnectDelayMs;
    this.delayFn =
      options.delayFn ??
      ((ms) => new Promise((resolve) => {
        setTimeout(resolve, ms);
      }));
  }

  // ─── Public Methods ──────────────────────────────────────────────────────

  /**
   * Enqueue a change to the sync queue. Items are stored with their timestamp
   * and processed in chronological order during flush.
   *
   * @param item - The ExtendedSyncQueueItem to enqueue (id is auto-generated)
   * @throws Error if the queue is already at max capacity (1000 items)
   */
  async enqueue(item: Omit<ExtendedSyncQueueItem, 'id'>): Promise<void> {
    const size = await getSyncQueueSize();
    if (size >= MAX_SYNC_QUEUE_SIZE) {
      throw new Error(
        `Sync queue is full: maximum ${MAX_SYNC_QUEUE_SIZE} queued changes allowed. Connect to sync.`
      );
    }

    await db.syncQueue.add(item as unknown as SyncQueueItem);
    await this.notifyQueueSizeChange();
  }

  /**
   * Get the current number of items in the queue.
   */
  async getQueueSize(): Promise<number> {
    return getSyncQueueSize();
  }

  /**
   * Flush the sync queue: process all items in chronological order (by timestamp)
   * regardless of module, routing each to the correct API endpoint.
   *
   * Items are sorted by timestamp to preserve causal ordering across modules.
   * On failure, the item's retryCount is incremented and exponential backoff is applied.
   */
  async flush(): Promise<void> {
    if (this.isProcessing || this.isStopped) {
      return;
    }

    this.isProcessing = true;
    this.currentBackoff = INITIAL_BACKOFF_MS;

    try {
      await this.processQueue();
    } finally {
      this.isProcessing = false;
    }
  }

  /**
   * Handle connectivity being restored. Begins sync within 10 seconds.
   */
  handleReconnect(): void {
    if (this.isProcessing || this.isStopped) {
      return;
    }

    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
    }

    const delay =
      this.reconnectDelayMs ??
      Math.min(Math.random() * RECONNECT_SYNC_DELAY_MS, RECONNECT_SYNC_DELAY_MS);

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      void this.flush();
    }, delay);
  }

  /**
   * Stop the sync queue processing. Clears all timers.
   */
  stop(): void {
    this.isStopped = true;
    this.isProcessing = false;

    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  /**
   * Resume the sync queue after being stopped.
   */
  resume(): void {
    this.isStopped = false;
  }

  /**
   * Check if the queue is currently processing.
   */
  get processing(): boolean {
    return this.isProcessing;
  }

  // ─── Private Methods ─────────────────────────────────────────────────────

  /**
   * Process all items in the queue in chronological order (by timestamp).
   * Routes each item to the correct API endpoint based on module field.
   */
  private async processQueue(): Promise<void> {
    while (!this.isStopped) {
      // Get all items sorted by timestamp (chronological order across all modules)
      const items = await db.syncQueue.orderBy('timestamp').toArray();

      if (items.length === 0) {
        this.onSyncComplete?.();
        await this.notifyQueueSizeChange();
        break;
      }

      // Process the oldest item first (chronological ordering)
      const rawItem = items[0]!;
      const item = rawItem as unknown as ExtendedSyncQueueItem & { id: number };

      const success = await this.processItem(item);

      if (success) {
        // Remove successfully processed item from queue
        if (rawItem.id != null) {
          await db.syncQueue.delete(rawItem.id as unknown as number);
        }
        this.currentBackoff = INITIAL_BACKOFF_MS;
        await this.notifyQueueSizeChange();
      } else {
        // Increment retry count in place
        if (rawItem.id != null) {
          await db.syncQueue.update(rawItem.id as unknown as number, {
            retryCount: ((item.retryCount as number) || 0) + 1,
          });
        }

        this.onItemError?.(item, new Error('Sync request failed'));
        await this.notifyQueueSizeChange();

        // Wait with exponential backoff
        await this.waitBackoff();

        // Double the backoff for next failure (capped at MAX_BACKOFF_MS)
        this.currentBackoff = Math.min(this.currentBackoff * 2, MAX_BACKOFF_MS);
      }
    }
  }

  /**
   * Process a single queue item by routing it to the correct API endpoint
   * based on its module discriminator.
   *
   * @returns true if the request succeeded, false otherwise.
   */
  private async processItem(item: ExtendedSyncQueueItem): Promise<boolean> {
    const endpoint = MODULE_ENDPOINTS[item.module];
    const method = ACTION_TO_METHOD[item.action];

    if (!endpoint || !method) {
      // Unknown module or action — cannot process, treat as success to avoid infinite loop
      return true;
    }

    try {
      await this.apiClient.sendRequest(
        endpoint,
        method,
        item.entityId,
        item.payload
      );
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Wait for the current backoff duration.
   */
  private async waitBackoff(): Promise<void> {
    if (this.isStopped) {
      return;
    }
    await this.delayFn(this.currentBackoff);
  }

  /**
   * Notify the onQueueSizeChange callback with the current queue size.
   */
  private async notifyQueueSizeChange(): Promise<void> {
    if (this.onQueueSizeChange) {
      const size = await getSyncQueueSize();
      this.onQueueSizeChange(size);
    }
  }
}

// ─── Exponential Backoff Utility (exported for testing) ──────────────────────

/**
 * Calculate the backoff delay for a given retry attempt.
 * Uses exponential backoff: delay = min(INITIAL_BACKOFF_MS * 2^attempt, MAX_BACKOFF_MS)
 *
 * @param attempt - The retry attempt number (0-indexed)
 * @returns The delay in milliseconds
 */
export function calculateBackoff(attempt: number): number {
  return Math.min(INITIAL_BACKOFF_MS * Math.pow(2, attempt), MAX_BACKOFF_MS);
}
